/**
 * Normative operation budget — the executable form of `docs/day0/OPERATION-BUDGET.md`.
 *
 * Every gas figure here is MEASURED against the real local Nox stack during Day 0, not estimated.
 * Raw data: `evidence/day0/nox-runtime/`. These values constrain the confidential curve engine
 * that Phase 2 builds; Phase 1 exports them so the keeper, the workflow step planner and the
 * deployment manifests are sized against measured reality rather than a guess.
 *
 * ---------------------------------------------------------------------------------------------
 * PHASE 1 CORRECTION (delta P-1, docs/phase1/PRD-DELTA.md)
 *
 * The capacity table published in OPERATION-BUDGET.md section 5 is generated by the model
 *
 *     cells x accumulateCell  +  providers x (cacheProvider + allocate)  +  leaves x (finalizeLeaf + reduceWinnerLeaf)
 *
 * which reproduces every published row to the exact wei. That model omits two costs that are
 * genuinely incurred once per epoch: Stage C's per-chunk fixed overhead (166,954 gas x chunk
 * count) and Stage E2 `publishWinner` (90,076 gas). Per-epoch gas is therefore understated by
 * 0.72% at 16x128 and 2.42% at 4x16.
 *
 * `publishedEpochGas()` reproduces the Day 0 table exactly so the evidence stays checkable.
 * `planEpoch()` is the corrected model and is what production code must use. The Day 0
 * conclusion is unchanged: the full 16x128 universe remains executable.
 *
 * Stage A `seedProvider` is excluded from both, deliberately and correctly — a provider mandate
 * is sealed once and reused across every subsequent epoch, so it is not an epoch cost. It is
 * exported separately as `STAGE_GAS.seedProvider` for mandate-submission sizing.
 * ---------------------------------------------------------------------------------------------
 */
/** Marginal gas per Nox primitive, measured as (gas(n=10) - gas(n=1)) / 9. */
export const PRIMITIVE_GAS = {
    allow: 3_651,
    allowTransient: 4_323,
    allowThis: 5_856,
    toEuint16: 6_256,
    toEuint256: 6_256,
    ge: 10_110,
    eq: 10_398,
    lt: 10_398,
    add: 10_377,
    add16: 12_225,
    mul16: 12_225,
    sub: 12_285,
    mul: 12_285,
    div: 12_285,
    select16: 13_300,
    select256: 15_263,
    safeAdd: 15_453,
    safeSub: 15_453,
    safeMul: 15_563,
    safeDiv: 15_563,
};
/** Composites Kyrve actually needs. Nox has no boolean operations; both are arithmetised. */
export const COMPOSITE_GAS = {
    /** ebool -> euint16 0/1. The only way to combine predicates. */
    indicator: 10_625,
    /** One unoptimised eligibility cell: 6 selects + 5 muls + 1 eq. Superseded by the cached form. */
    naiveSixTermConjunction: 146_865,
};
/** Measured cost of each hierarchical epoch stage (PRD v1.1 A-10). */
export const STAGE_GAS = {
    /** Stage A, per provider. Paid once at mandate submission, NOT once per epoch. */
    seedProvider: 348_830,
    /** Stage B, per provider — NOT per leaf. This is what makes the universe executable. */
    cacheProvider: 256_553,
    /** Stage C, per (provider, leaf) cell: ge -> select256 -> add -> select16 -> add16. */
    accumulateCell: 76_402,
    /** Stage C fixed overhead, per chunk transaction. */
    accumulateChunkOverhead: 166_954,
    /** Stage D, per leaf. Applies the privacy floor. */
    finalizeLeaf: 158_847,
    /** Stage E, per leaf. */
    reduceWinnerLeaf: 94_649,
    /** Stage E2, once per epoch. The ONE public/private boundary crossing. */
    publishWinner: 90_076,
    /** Stage F, per provider. */
    allocate: 166_423,
};
/** Binding limits. Exceeding any of these is a bug, not a tuning choice. */
export const OPERATION_BUDGET = {
    /** 80% of a 30M block, leaving headroom. */
    transactionGasCeiling: 24_000_000,
    /** floor((24_000_000 - 166_954) / 76_402). Derived from measurement, not chosen. */
    maxCellsPerTransaction: 311,
    /** 19.7M gas per chunk, ~18% margin under the ceiling. */
    recommendedChunkCells: 256,
    maxProvidersPerChunk: 16,
    maxLeavesPerReduceChunk: 64,
    maxLeavesPerFinalizeChunk: 128,
    /** ge + two select results. Budget this when sizing storage and gateway load. */
    handlesPerCell: 3,
    /** Measured p90 was 492 ms locally; this is a 10x margin. Testnet is UNVERIFIED (AS-1). */
    runnerTimeoutPerStageMs: 5_000,
    /** Cloudflare Workflows default. */
    stepRetryLimit: 5,
    /** Workflow wall clock for cron, queue consumers and Durable Object alarms. */
    epochTimeoutMs: 15 * 60 * 1000,
    /** Workers allow 6 simultaneous connections awaiting response headers. */
    maxRpcConcurrency: 6,
    minKeeperConcurrency: 2,
};
/** The PRD 9.1 launch universe, retained in full. No parameter reduction, no deferred pillar. */
export const LAUNCH_UNIVERSE = {
    maxProviders: 16,
    maxLeaves: 128,
};
function chunk(total, per) {
    const sizes = [];
    let remaining = total;
    while (remaining > 0) {
        const take = Math.min(per, remaining);
        sizes.push(take);
        remaining -= take;
    }
    return sizes;
}
/**
 * Produces the real, stage-aware transaction schedule for one epoch.
 *
 * This is what the keeper plans against. Stages cannot be merged across a transaction boundary
 * because each is a distinct external call with its own idempotency key, so the transaction count
 * is derived from a genuine schedule rather than by dividing total gas by the ceiling.
 *
 * @param cellsPerChunk defaults to the recommended 256, not the 311 maximum, so a chunk retains
 *        roughly 18% headroom against the gas ceiling.
 */
export function planEpoch(providers, leaves, cellsPerChunk = OPERATION_BUDGET.recommendedChunkCells) {
    assertPositiveInteger(providers, "providers");
    assertPositiveInteger(leaves, "leaves");
    assertChunkWithinBudget(cellsPerChunk);
    const transactions = [];
    transactions.push({
        stage: "cacheProvider",
        chunkIndex: 0,
        units: providers,
        gas: providers * STAGE_GAS.cacheProvider,
    });
    const cells = providers * leaves;
    chunk(cells, cellsPerChunk).forEach((size, i) => {
        transactions.push({
            stage: "accumulate",
            chunkIndex: i,
            units: size,
            gas: size * STAGE_GAS.accumulateCell + STAGE_GAS.accumulateChunkOverhead,
        });
    });
    chunk(leaves, OPERATION_BUDGET.maxLeavesPerFinalizeChunk).forEach((size, i) => {
        transactions.push({
            stage: "finalize",
            chunkIndex: i,
            units: size,
            gas: size * STAGE_GAS.finalizeLeaf,
        });
    });
    chunk(leaves, OPERATION_BUDGET.maxLeavesPerReduceChunk).forEach((size, i) => {
        transactions.push({
            stage: "reduce",
            chunkIndex: i,
            units: size,
            gas: size * STAGE_GAS.reduceWinnerLeaf,
        });
    });
    transactions.push({
        stage: "publish",
        chunkIndex: 0,
        units: 1,
        gas: STAGE_GAS.publishWinner,
    });
    transactions.push({
        stage: "allocate",
        chunkIndex: 0,
        units: providers,
        gas: providers * STAGE_GAS.allocate,
    });
    const totalGas = transactions.reduce((sum, t) => sum + t.gas, 0);
    const peakTransactionGas = transactions.reduce((max, t) => Math.max(max, t.gas), 0);
    if (peakTransactionGas > OPERATION_BUDGET.transactionGasCeiling) {
        throw new Error(`Epoch plan for ${providers}x${leaves} has a ${peakTransactionGas} gas transaction, above the ` +
            `${OPERATION_BUDGET.transactionGasCeiling} ceiling. Reduce cellsPerChunk or the universe size.`);
    }
    return {
        providers,
        leaves,
        cells,
        cellGas: cells * STAGE_GAS.accumulateCell,
        totalGas,
        transactions,
        transactionCount: transactions.length,
        peakTransactionGas,
    };
}
/**
 * Reproduces the capacity table published in docs/day0/OPERATION-BUDGET.md section 5, exactly.
 *
 * Retained so the Day 0 evidence stays checkable against this repository. It is NOT the model
 * production code should use — see the Phase 1 correction at the top of this file.
 */
export function publishedEpochGas(providers, leaves) {
    assertPositiveInteger(providers, "providers");
    assertPositiveInteger(leaves, "leaves");
    return (providers * leaves * STAGE_GAS.accumulateCell +
        providers * (STAGE_GAS.cacheProvider + STAGE_GAS.allocate) +
        leaves * (STAGE_GAS.finalizeLeaf + STAGE_GAS.reduceWinnerLeaf));
}
/** Throws if a proposed chunk would exceed the measured per-transaction budget. */
export function assertChunkWithinBudget(cells) {
    assertPositiveInteger(cells, "cells");
    if (cells > OPERATION_BUDGET.maxCellsPerTransaction) {
        throw new Error(`Chunk of ${cells} cells exceeds the measured budget of ` +
            `${OPERATION_BUDGET.maxCellsPerTransaction} cells per transaction ` +
            `(${OPERATION_BUDGET.transactionGasCeiling} gas ceiling, ${STAGE_GAS.accumulateCell} gas per cell, ` +
            `${STAGE_GAS.accumulateChunkOverhead} chunk overhead). docs/day0/OPERATION-BUDGET.md is binding.`);
    }
}
function assertPositiveInteger(value, name) {
    if (!Number.isInteger(value) || value <= 0) {
        throw new Error(`${name} must be a positive integer, received ${value}`);
    }
}
//# sourceMappingURL=budget.js.map